import React from 'react';
import {
	AlertErrorIcon,
	ArrowDownIcon,
	ArrowRightIcon,
	Button,
	CircleWarningIcon,
	LoadingIconPrimary,
	palette,
	typography,
} from '@citrite/web-ui-component';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { trackAnalyticsEvent } from 'analytics';
import { t } from 'i18next';
import { LaunchDetails } from 'App/ProgressAlert/LaunchDetails';
import {
	CardDivStyle,
	StyledAlertAction,
	StyledAlertContent,
	StyledAlertSection,
	StyledStatus,
	StyledStatusText,
} from 'App/ProgressAlert/LaunchProgressAlert.styles';
import { getLaunchProgressStatus } from 'App/ProgressAlert/utils';
import { ResourceIcon } from 'Components/ResourceIcon';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { LaunchProgressDetails } from 'Workspace/ResourceProvider/Context';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { TroubleshootEventPayload } from 'Workspace/TelemetryEvents/troubleshoot/troubleshootCASReporter';

type CardProps = {
	isLast: Boolean;
};
const StyledCard = styled.div<CardProps>`
	${CardDivStyle}
	height: 64px;
	width: auto;
	border-bottom: ${props => (props.isLast ? '' : `1px solid ${palette.grey200}`)};
	flex-shrink: 0;
`;
const StyledTitle = styled.span`
	font-size: ${typography.sizes.base};
	line-height: 18px;
	font-weight: ${typography.weights.medium};
`;
const StyledAlertStatus = styled(StyledStatus)`
	margin-top: 12px;
	gap: 8px;
	align-items: center;
`;

const dotsAnimation = keyframes`
	0% { 
		content: "." ;
	}
	33% {
		content: ".."; 
	}
	66% {
		content: "..."; 
	}
	100% {
		content: ".";
	}
`;

export const StyledLoadingDots = styled.span`
	display: inline-block;
	width: 10px;
	text-align: left;
	&::after {
		content: '.';
		animation: ${dotsAnimation} 1.5s infinite steps(1);
	}
`;

const StyledArroRightIcon = styled(ArrowRightIcon)`
	cursor: pointer;
`;

const StyledArroDownIcon = styled(ArrowDownIcon)`
	cursor: pointer;
`;

export interface ResourceAlertProps {
	launchProgressContext: LaunchProgressDetails;
	isLast: Boolean;
	operationId: string;
	error: string;
	resource: Resource;
	onCancel: (operationId: string) => void;
	onClose: (operationId: string) => void;
	isExpanded: boolean;
	onToggleExpand: () => void;
}
export function ResourceAlert({
	launchProgressContext,
	isLast,
	operationId,
	onCancel,
	onClose,
	resource,
	error,
	isExpanded,
	onToggleExpand,
}: ResourceAlertProps) {
	const launchStatus = getLaunchProgressStatus(launchProgressContext);
	return (
		!!resource && (
			<>
				<StyledCard data-testid={'resource-alert' + resource.name} isLast={isLast}>
					<StyledAlertContent>
						<ResourceIcon resource={resource} size={27} />
						<StyledAlertSection>
							<StyledTitle>{resource.name}</StyledTitle>
							<StyledAlertStatus>
								{error ? (
									<>
										{launchProgressContext?.troubleshoot ? (
											<>
												<CircleWarningIcon size={15} color={palette.yellow800} />
												<StyledStatusText color={palette.yellow800}>
													{t('Workspace:launch_status.issue_found')}
												</StyledStatusText>
											</>
										) : (
											<>
												<AlertErrorIcon size={15} color={palette.red800} />
												<StyledStatusText color={palette.red800}>
													{t('Workspace:launch_status.failed')}
												</StyledStatusText>
											</>
										)}
										{isExpanded ? (
											<StyledArroDownIcon size={12} onClick={onToggleExpand} />
										) : (
											<StyledArroRightIcon size={12} onClick={onToggleExpand} />
										)}
									</>
								) : (
									<>
										<LoadingIconPrimary size={15} />
										<StyledStatusText>
											{t(`Workspace:launch_status.${launchStatus}`)}
											<StyledLoadingDots />
										</StyledStatusText>
									</>
								)}
							</StyledAlertStatus>
						</StyledAlertSection>
					</StyledAlertContent>
					{!error ? (
						<StyledAlertAction>
							<Button.SubtlePrimary
								disabled={
									launchStatus === LaunchStatus.PerformingLaunch ||
									launchStatus === LaunchStatus.Cancelling
								}
								onClick={() => onCancel(operationId)}
							>
								{t('Workspace:cancel')}
							</Button.SubtlePrimary>
						</StyledAlertAction>
					) : (
						<StyledAlertAction>
							<Button.SubtlePrimary
								onClick={() => {
									trackAnalyticsEvent(
										TroubleshootEventPayload.closeOnError({
											transactionId: operationId,
											resourceName: resource.name,
											errorMessage: error,
										})
									);
									onClose(operationId);
								}}
							>
								{t('Workspace:close')}
							</Button.SubtlePrimary>
						</StyledAlertAction>
					)}
				</StyledCard>
				{isExpanded && error && (
					<LaunchDetails
						transactionId={operationId}
						resource={resource}
						errorMessage={error}
						isTroubleshoot={launchProgressContext?.troubleshoot}
						onClose={onClose}
					/>
				)}
			</>
		)
	);
}
